import { useEffect, useState } from "react";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import BillingDialog from "@/components/BillingDialog";
import { FadeUp, SectionLabel } from "@/components/Reveal";
import { api, createCheckout, formatPrice, formatApiError, useContent } from "@/lib/api";

export default function Packages() {
  const content = useContent();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(null);
  const [buying, setBuying] = useState(null);
  const [checkoutUrl, setCheckoutUrl] = useState(null);

  useEffect(() => {
    api.get("/products", { params: { type: "coaching" } }).then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const handleBuy = async (billing) => {
    setLoading(buying.slug);
    try {
      const url = await createCheckout(buying.slug, billing);
      setCheckoutUrl(url);
      setLoading(null);
    } catch (e) {
      toast.error(formatApiError(e));
      setLoading(null);
      setBuying(null);
    }
  };

  return (
    <div data-testid="packages-page">
      <Nav />
      <section className="mx-auto max-w-7xl px-6 pb-28 pt-40 lg:px-12 lg:pt-52">
        <SectionLabel>Pacchetti di coaching</SectionLabel>
        <FadeUp as="h1" delay={0.1} className="mt-6 max-w-3xl font-display text-5xl leading-[1.05] text-ivory md:text-7xl" data-testid="packages-title">
          Scegli il tuo
          <span className="italic text-mist"> percorso.</span>
        </FadeUp>
        <FadeUp delay={0.2} className="mt-8 max-w-xl text-sm leading-loose text-mute md:text-base">
          Dalla scheda singola al percorso annuale: cinque modi per allenarti con me, un unico metodo.
          Ogni pacchetto è un servizio di coaching: dopo l'acquisto riceverai le istruzioni per iniziare.
        </FadeUp>

        <div className="mt-20 grid gap-8 lg:grid-cols-3" data-testid="packages-grid">
          {products.map((p, i) => (
            <FadeUp
              key={p.slug}
              delay={i * 0.1}
              className={`group flex flex-col border p-10 transition-all duration-500 hover:-translate-y-2 ${
                p.slug === "coaching-3-mesi" ? "border-sage bg-surface" : "border-line bg-transparent hover:border-mist/40"
              }`}
              data-testid={`package-card-${p.slug}`}
            >
              {p.slug === "coaching-3-mesi" && (
                <span className="label-caps mb-6 w-fit bg-sage px-3 py-1 text-ivory">Più scelto</span>
              )}
              <h3 className="font-display text-3xl text-ivory">{p.name}</h3>
              <p className="mt-3 text-sm leading-relaxed text-mute">{p.subtitle}</p>
              <p className="mt-8 font-display text-4xl text-ivory" data-testid={`package-price-${p.slug}`}>
                {formatPrice(p.price_cents, p.currency)}
                <span className="ml-2 text-xs uppercase tracking-widest text-mute">/ percorso</span>
              </p>
              <ul className="mt-8 flex flex-1 flex-col gap-4 border-t border-line pt-8">
                {(p.features || []).map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-ivory/80">
                    <Check size={16} className="mt-0.5 shrink-0 text-sage" />
                    {f}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => setBuying(p)}
                disabled={loading === p.slug}
                data-testid={`package-buy-${p.slug}`}
                className={`mt-10 ${p.slug === "coaching-3-mesi" ? "btn-primary" : "btn-outline"} w-full disabled:opacity-60`}
              >
                {loading === p.slug ? <Loader2 size={14} className="animate-spin" /> : "Scegli questo percorso"}
              </button>
            </FadeUp>
          ))}
        </div>
        <FadeUp delay={0.2} className="mt-12 text-xs text-mute">
          Pagamento sicuro online. Dopo l'acquisto riceverai via email le istruzioni per iniziare.
        </FadeUp>
      </section>
      <BillingDialog
        open={!!buying}
        onClose={() => { setBuying(null); setCheckoutUrl(null); }}
        onConfirm={handleBuy}
        loading={!!loading}
        productName={buying?.name || ""}
        checkoutUrl={checkoutUrl}
      />
      <Footer content={content} />
    </div>
  );
}
